// =============================================================================
// Webhook rule config validation + SSRF URL rule (WU-3.2 T1).
// Pure, I/O-free — this package runs client-side too (no fetch, no node-only
// APIs). `isForbiddenWebhookUrl` is THE single URL rule; Task 3 reuses it
// api-side at call time so the save-time and dial-time checks never drift.
// =============================================================================
const MIN_TIMEOUT_MS = 1000;
const MAX_TIMEOUT_MS = 30000;
const VALID_FAIL_MODES = new Set(['soft', 'hard']);
const VALID_ON_UNRESOLVED = new Set(['soft', 'escalate', 'hold']);
const VALID_TRANSPORTS = new Set(['auto', 'fast', 'durable']);
const VALID_CONFIG_SEVERITIES = new Set(['error', 'warning']);
// S-5 §1(a)/design descope note — the triggers a webhook rule may legally
// attach to in v1: field/blur (on-change), accept (on-validate), and
// step-enter. on-schedule/on-batch-classified/on-batch-complete are
// batch-scope checkpoint triggers with no fieldRef-scoped meaning for a
// field-scoped webhook check. on-export is deliberately NOT included here —
// see the dedicated check below.
const VALID_WEBHOOK_TRIGGERS = new Set(['on-change', 'on-validate', 'on-step-enter']);
/** Legacy + current spellings that mark a rule's config as a webhook
 *  validation. `rule.type` is cast to `string` because `'webhook_validate'` is
 *  a legacy `RuleActionType` spelling, not a member of the current `RuleType`
 *  union — a direct literal comparison would be a compile-time no-overlap
 *  error. Exported (WU-3.2 T2) so the rule-runtime batch gate can reuse the
 *  same predicate instead of maintaining its own copy. */
export function isWebhookRule(rule) {
    const type = rule.type;
    if (type === 'webhook' || type === 'webhook_validate')
        return true;
    const validationType = rule.config?.validationType;
    return validationType === 'webhook';
}
/** S-5 batched-review final round (B1) — explicit opt-in predicate for the
 *  any-timing async webhook-check lane (field/blur, accept, step-enter —
 *  the S-5 collector, `collectWebhookChecksForTiming`). Every automation-
 *  builder webhook rule compiles with a hardcoded `trigger:'on-validate'`
 *  (`check-effect.ts`'s `makeConfigCheck`) — historically inert because the
 *  Tier-3 batch gate (`run-content-batch-gate.ts` pass 2b, UNTOUCHED by this
 *  predicate) never filtered on trigger. Without this gate, EVERY existing
 *  published webhook rule would be collected the moment S-5 deploys,
 *  flipping dormant config into live dials / BullMQ jobs / accept blocks on
 *  the very next accept. A rule opts in by carrying at least one of the
 *  S-5-introduced config keys EXPLICITLY — `severity`, `onUnresolved`,
 *  `transport`, `timeoutMs` — or the dedicated `asyncCheck: true` flag for a
 *  rule that wants the async lane at its (legacy) defaults with no other
 *  config change. A config-less legacy rule returns `false` and stays
 *  batch-gate-only, byte-identical to pre-S-5.
 *
 *  THE single predicate — used by the collector (closes the accept gate,
 *  the on-change/on-validate/on-step-enter dial, and dispatch, since all
 *  three key off `collectWebhookChecksForTiming`) AND by this file's own
 *  trigger-legality publish gate below (so a legacy draft with an
 *  out-of-vocabulary trigger — legal before S-5 introduced this check —
 *  stays publishable as long as it never opted in). Never duplicate this
 *  check inline; import it. */
export function isAsyncCheckOptedIn(rule) {
    if (!isWebhookRule(rule))
        return false;
    const config = rule.config;
    if (!config)
        return false;
    return (config.severity !== undefined ||
        config.onUnresolved !== undefined ||
        config.transport !== undefined ||
        config.timeoutMs !== undefined ||
        config.asyncCheck === true);
}
/** True when `v` is a redaction marker left by config export — the signal
 *  that an imported draft needs its credential re-entered before publish.
 *  `redacted: true` can only be authored by `redactContentCredentials`
 *  (P-13+); no pre-existing content carries it, so validators may act on it
 *  unconditionally with zero legacy-flip risk. */
export function isRedactedWebhookAuth(v) {
    return (!!v &&
        typeof v === 'object' &&
        v.redacted === true &&
        typeof v.type === 'string');
}
const REDACTED_AUTH_LABELS = {
    bearer: 'bearer token',
    basic: 'basic-auth password',
    api_key: 'API key',
};
/** Human label for the secret a redacted auth config is missing. */
export function redactedAuthLabel(auth) {
    return REDACTED_AUTH_LABELS[auth.type] ?? 'credential';
}
/**
 * True if the given hostname looks like a numeric IPv4 address (all-digit
 * dotted quad). Used to decide whether to apply the private-range octet
 * check vs. the bare-hostname / single-label checks.
 */
function parseIpv4Octets(host) {
    const parts = host.split('.');
    if (parts.length !== 4)
        return null;
    const octets = [];
    for (const p of parts) {
        if (!/^\d{1,3}$/.test(p))
            return null;
        // Reject leading-zero multi-digit octets (e.g. "010") — an octal-style
        // deceptive encoding, per the assert-connectable-host.ts precedent.
        if (p.length > 1 && p.startsWith('0'))
            return null;
        const n = Number(p);
        if (n > 255)
            return null;
        octets.push(n);
    }
    return octets;
}
/** True if the IPv4 octets fall in a private, loopback, link-local, or
 *  "this network" (0.*) range — checked by octet, not regex, per spec. */
function isPrivateIpv4Octets(o) {
    const [a, b] = o;
    if (a === 10)
        return true;
    if (a === 127)
        return true;
    if (a === 169 && b === 254)
        return true;
    if (a === 172 && b >= 16 && b <= 31)
        return true;
    if (a === 192 && b === 168)
        return true;
    if (a === 0)
        return true;
    return false;
}
/**
 * Decodes the embedded 32-bit IPv4 address from an IPv4-mapped
 * (`::ffff:a.b.c.d`) or IPv4-compatible (`::a.b.c.d`, deprecated) IPv6
 * literal, returning its 4 octets — or `null` if the host isn't one of
 * those shapes. Handles BOTH textual forms a caller might hand us:
 *  - dotted tail (`::ffff:169.254.169.254`, `::127.0.0.1`) — the literal a
 *    human would type.
 *  - hex tail (`::ffff:a9fe:a9fe`, `::7f00:1`) — what the WHATWG `URL`
 *    parser actually canonicalizes the dotted form TO (confirmed empirically:
 *    `new URL("https://[::ffff:169.254.169.254]/x").hostname` is
 *    `[::ffff:a9fe:a9fe]`, never the dotted spelling). A hextet is 1-4 hex
 *    digits.
 * The `ffff:` prefix is optional in both regexes so one function covers the
 * mapped (`::ffff:...`, the common modern form) and compatible (`::...`,
 * legacy, no `ffff`) variants alike.
 */
function extractEmbeddedIpv4(host) {
    const dotted = /^::(?:ffff:)?(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/.exec(host);
    if (dotted)
        return parseIpv4Octets(dotted[1]);
    const hex = /^::(?:ffff:)?([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(host);
    if (hex) {
        const hi = parseInt(hex[1], 16);
        const lo = parseInt(hex[2], 16);
        return [(hi >> 8) & 0xff, hi & 0xff, (lo >> 8) & 0xff, lo & 0xff];
    }
    return null;
}
/** True if the (already bracket-stripped, lowercased) host is a forbidden
 *  IPv6 literal: unspecified (::), loopback (::1), unique-local (fc00::/7),
 *  link-local (fe80::/10), a NAT64 translation prefix (64:ff9b::/96 per RFC
 *  6052, 64:ff9b:1::/48 per RFC 8215), or an IPv4-mapped/-compatible literal
 *  whose embedded IPv4 is itself private/loopback/link-local. The fc00/fe80
 *  ranges are matched on the FIRST hextet, which is always written in full (4
 *  hex digits): its top nibble is `f` (non-zero) so IPv6 compression — which
 *  only drops LEADING zeros within a hextet — never shortens it. Requiring
 *  exactly 4 digits before the ':' avoids over-matching legitimate non-ULA
 *  hosts like `fc:1::` (first hextet 0x00fc) or `fca::1` (0x0fca), which fall
 *  outside fc00-fdff.
 *
 *  NAT64 prefixes are forbidden outright rather than decoded: they are
 *  IANA-reserved translation prefixes with no legitimate webhook use, so
 *  there is no genuinely-public address to preserve access to (unlike the
 *  IPv4-mapped case below).
 *
 *  IPv4-mapped/-compatible literals are NOT forbidden outright — the
 *  embedded IPv4 is decoded and re-run through the same
 *  `isPrivateIpv4Octets` range check the plain-IPv4 path uses, so a
 *  genuinely-public mapped address (e.g. `::ffff:8.8.8.8`) is ALLOWED,
 *  matching how a plain `8.8.8.8` host is treated. Pinned choice — the
 *  simpler "forbid all mapped addresses" alternative was considered and
 *  rejected as inconsistent with the rest of this module's by-range
 *  philosophy. */
function isForbiddenIpv6Literal(host) {
    if (host === '::' || host === '::1')
        return true; // unspecified / loopback
    if (/^f[cd][0-9a-f]{2}:/.test(host))
        return true; // fc00::/7 (first hextet fc00-fdff)
    if (/^fe[89ab][0-9a-f]:/.test(host))
        return true; // fe80::/10 (first hextet fe80-febf)
    if (host.startsWith('64:ff9b:'))
        return true; // NAT64 (RFC 6052 /96, RFC 8215 /48) — reserved, always embeds IPv4
    const embedded = extractEmbeddedIpv4(host);
    if (embedded && isPrivateIpv4Octets(embedded))
        return true;
    return false;
}
/**
 * Parses a URL using the ambient global `URL` constructor (present in both
 * browsers and Node) without depending on DOM or Node lib types — this
 * package's tsconfig targets a bare ES2022 lib so it stays portable to the
 * client bundle (mirrors the `globalThis` cast used for `crypto.randomUUID`
 * in rule-attachment.ts). Returns null on a malformed URL instead of throwing.
 */
function parseUrl(url) {
    try {
        const UrlCtor = globalThis.URL;
        return new UrlCtor(url);
    }
    catch {
        return null;
    }
}
/**
 * THE single URL rule for webhook targets. Returns a problem string when the
 * url is forbidden, or null when it's an acceptable outbound target.
 * Enforces: must parse as a URL; protocol must be `https:`; hostname must not
 * be `localhost`, must not end in `.railway.internal`, must not be a bare
 * single-label hostname (no dot), and must not be a private/loopback/
 * link-local IP literal (IPv4 by octet parsing; IPv6 literal ranges).
 */
export function isForbiddenWebhookUrl(url) {
    const parsed = parseUrl(url);
    if (!parsed) {
        return `"${url}" is not a valid URL.`;
    }
    if (parsed.protocol !== 'https:') {
        return `Webhook URL must use https — got "${parsed.protocol}".`;
    }
    const host = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, '');
    if (host === 'localhost') {
        return 'Webhook URL host must not be localhost.';
    }
    if (host.endsWith('.railway.internal')) {
        return 'Webhook URL host must not target internal Railway infrastructure.';
    }
    // IPv6 literal (URL.hostname keeps the brackets for IPv6; already stripped above).
    if (host.includes(':')) {
        if (isForbiddenIpv6Literal(host)) {
            return `Webhook URL host "${host}" is a private/loopback/link-local IPv6 address.`;
        }
        return null;
    }
    const ipv4 = parseIpv4Octets(host);
    if (ipv4) {
        if (isPrivateIpv4Octets(ipv4)) {
            return `Webhook URL host "${host}" is a private/loopback/link-local IPv4 address.`;
        }
        return null;
    }
    // Anything composed solely of digits and dots is an IPv4 attempt; if it did
    // not parse as a clean dotted quad above (e.g. "010.0.0.1" octal-style, or
    // a bare integer) it is a deceptive encoding and is forbidden — matching the
    // assert-connectable-host.ts precedent rather than falling through as a name.
    if (/^[0-9.]+$/.test(host)) {
        return `Webhook URL host "${host}" is a malformed numeric address (deceptive IPv4 encoding).`;
    }
    if (!host.includes('.')) {
        return `Webhook URL host "${host}" is a bare single-label hostname — a fully-qualified domain is required.`;
    }
    return null;
}
/**
 * Validate a form's field-scoped webhook rules. Pure; returns issues (empty =
 * valid). Walks `form.rules`, selecting rules whose `config.validationType`
 * is `"webhook"` (or whose legacy `type` is `webhook`/`webhook_validate`),
 * and checks:
 *  - `url` via `isForbiddenWebhookUrl`
 *  - `timeoutMs`, if present, must be within [1000, 30000]
 *  - `failMode`, if present, must be `"soft"` or `"hard"`
 * Non-webhook rules are ignored.
 */
export function validateWebhookRuleConfig(form) {
    const issues = [];
    for (const rule of form.rules) {
        if (!isWebhookRule(rule))
            continue;
        const config = rule.config;
        const fieldRef = rule.fieldRef ?? null;
        // P-13 — an imported bundle never carries the auth secret; publishing a
        // rule whose auth is still the redaction marker would dial the receiver
        // with garbage credentials. Unconditional (NOT gated on
        // isAsyncCheckOptedIn): the marker only exists in P-13+ exports, so no
        // legacy published rule can be flipped by this check.
        if (isRedactedWebhookAuth(config.auth)) {
            issues.push({
                ruleName: rule.name,
                fieldRef,
                problem: `auth credential (${redactedAuthLabel(config.auth)}) was redacted when this config was exported — credentials never travel in config bundles. Re-enter it before publishing.`,
            });
        }
        if (typeof config.url === 'string') {
            const problem = isForbiddenWebhookUrl(config.url);
            if (problem) {
                issues.push({ ruleName: rule.name, fieldRef, problem });
            }
        }
        else {
            issues.push({ ruleName: rule.name, fieldRef, problem: 'Webhook rule is missing a url.' });
        }
        if (config.timeoutMs !== undefined) {
            const timeout = config.timeoutMs;
            if (typeof timeout !== 'number' ||
                !Number.isFinite(timeout) ||
                timeout < MIN_TIMEOUT_MS ||
                timeout > MAX_TIMEOUT_MS) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `timeoutMs must be between ${MIN_TIMEOUT_MS} and ${MAX_TIMEOUT_MS} — got ${String(timeout)}.`,
                });
            }
        }
        if (config.failMode !== undefined) {
            if (typeof config.failMode !== 'string' || !VALID_FAIL_MODES.has(config.failMode)) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `failMode must be "soft" or "hard" — got ${JSON.stringify(config.failMode)}.`,
                });
            }
        }
        if (config.severity !== undefined) {
            if (typeof config.severity !== 'string' || !VALID_CONFIG_SEVERITIES.has(config.severity)) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `severity must be "error" or "warning" — got ${JSON.stringify(config.severity)}.`,
                });
            }
        }
        if (config.onUnresolved !== undefined) {
            if (typeof config.onUnresolved !== 'string' || !VALID_ON_UNRESOLVED.has(config.onUnresolved)) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `onUnresolved must be "soft", "escalate", or "hold" — got ${JSON.stringify(config.onUnresolved)}.`,
                });
            }
        }
        if (config.transport !== undefined) {
            if (typeof config.transport !== 'string' || !VALID_TRANSPORTS.has(config.transport)) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `transport must be "auto", "fast", or "durable" — got ${JSON.stringify(config.transport)}.`,
                });
            }
        }
        // S-5 batched-review final round (B1, SECONDARY) — this whole
        // trigger-legality check (both branches) is a check introduced BY S-5;
        // before it existed, a webhook rule at ANY trigger was publishable (the
        // batch gate never filtered on trigger, so the trigger was inert). Gating
        // it behind isAsyncCheckOptedIn means a legacy config-less rule — even
        // one authored at an out-of-vocabulary trigger via some pre-S-5 path —
        // stays exactly as publishable as it always was; only a rule that
        // actually opted into the new async lane gets held to its vocabulary.
        if (isAsyncCheckOptedIn(rule)) {
            // S-5 §8 / design descope note — on-export CHECK-timing has no runtime
            // substrate in v1 (the on-export path evaluates action rules only, per
            // the design doc's ratified plan-time descope). Flagged distinctly from
            // the batch-scope-checkpoint rejection below so the message reads as
            // "not yet" rather than "never legal."
            if (rule.trigger === 'on-export') {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `trigger "on-export" is not yet supported for webhook checks in v1 — on-export check-timing is deferred (the on-export path runs action rules only). Use "on-change", "on-validate", or "on-step-enter" instead.`,
                });
            }
            else if (!VALID_WEBHOOK_TRIGGERS.has(rule.trigger)) {
                issues.push({
                    ruleName: rule.name,
                    fieldRef,
                    problem: `trigger "${rule.trigger}" is not a legal attachment for a webhook rule (allowed: ${[...VALID_WEBHOOK_TRIGGERS].join(', ')}).`,
                });
            }
        }
    }
    return issues;
}
/**
 * Resolves a `WebhookValidateConfig`'s S-5 any-timing defaults and the
 * `transport: 'auto'` derivation. Pure — no I/O. Precedent:
 * `resolveWebhookFailure` (`packages/rule-runtime/src/evaluate-content-rules.ts`)
 * resolves the TRANSPORT-FAILURE lane (unreachable receiver → soft/hard
 * routing past the batch gate); this resolves the CONFIG lane consumed
 * *before* a call is even placed — by the dialer (transport routing) and by
 * accept-time reconciliation (design §4).
 *
 * Defaults (design §2/§3): `severity ?? 'error'`, `onUnresolved ?? 'soft'`,
 * `timeoutMs ?? 10000`. `transport` — 'auto' (the config default, and what an
 * omitted value is treated as) resolves to `'durable'` when the resolved
 * `severity` is `'error'` OR the resolved `onUnresolved` is `'escalate'` or
 * `'hold'` (the block-shaped outcomes get the BullMQ worker's durability
 * guarantee); otherwise `'fast'` (api fire-and-forget). An explicit
 * `'fast'`/`'durable'` config value pins the choice and skips derivation
 * entirely, even when it disagrees with what auto-derivation would pick.
 *
 * TWO-SEVERITY PRECEDENCE (read before wiring a consumer): the `severity`
 * this function resolves is `WebhookValidateConfig.severity` — the S-5
 * async-webhook-lane severity that governs block-vs-annotate for THIS
 * check's own async resolution (pending/passed/failed/timeout). It is
 * entirely independent of `RuleAttachment.severity`, which governs the
 * synchronous field-validate lane (the rule's own severity, unrelated to the
 * webhook check's outcome). The two severities never arbitrate the same
 * decision — a webhook rule's field-validate severity and its async check
 * severity are separately authored and may differ. Do not conflate them, and
 * never fall one back to the other.
 */
export function resolveWebhookCheckConfig(config) {
    const severity = config.severity ?? 'error';
    const onUnresolved = config.onUnresolved ?? 'soft';
    const timeoutMs = config.timeoutMs ?? 10000;
    const transport = config.transport === 'fast' || config.transport === 'durable'
        ? config.transport
        : severity === 'error' || onUnresolved === 'escalate' || onUnresolved === 'hold'
            ? 'durable'
            : 'fast';
    return { severity, onUnresolved, timeoutMs, transport };
}
//# sourceMappingURL=webhook-rules.js.map